import Keycloak, { KeycloakInitOptions } from 'keycloak-js';
import { AuthConfig, AuthToken } from '../models';

let keycloak: Keycloak;

const createKeycloak = () => {
  keycloak = new Keycloak({
    url: process.env.REACT_APP_KEYCLOAK_URL,
    realm: process.env.REACT_APP_KEYCLOAK_REALM ?? '',
    clientId: process.env.REACT_APP_KEYCLOAK_CLIENT_ID ?? '',
  });
};

export const initKeycloak = async (
  authConfig: AuthConfig,
  initOptions: Partial<KeycloakInitOptions> = {},
) => {
  if (!keycloak) {
    createKeycloak();
  }

  return keycloak?.init({
    onLoad: 'login-required',
    pkceMethod: 'S256',
    responseMode: 'query',
    redirectUri: authConfig.redirectUrl,
    ...initOptions,
  });
};

const extractToken = () => ({
  jwt: keycloak.token ?? '',
  refreshJwt: keycloak.refreshToken ?? '',
});

export const getToken = async (authConfig: AuthConfig): Promise<AuthToken | undefined> => {
  if (!keycloak || keycloak?.loginRequired) {
    await initKeycloak(authConfig);
  }

  if (keycloak.authenticated) {
    return extractToken();
  }

  await keycloak.login();

  return undefined;
};

// Function to periodically check and renew the Keycloak token
export async function checkAndRenewToken() {
  try {
    await keycloak.updateToken(60);
    if (keycloak.authenticated) {
      return extractToken();
    }

    throw Error('[Auth] Problem relating to exchange token');
  } catch (error) {
    keycloak.clearToken();
    throw error;
  }
}

export async function renewToken() {
  const token = await checkAndRenewToken();
  return token?.jwt;
}

/**
 * Terminates the Keycloak SSO session.
 *
 * Resolves `true` when the browser has been sent to the Keycloak logout endpoint
 * (the caller must NOT navigate anywhere else — doing so aborts that navigation),
 * `false` when Keycloak reports no SSO session (the caller may go to the login page).
 *
 * When the adapter is not initialised in this page load (session resumed from
 * localStorage) it is initialised with `check-sso`, never `login-required`: the
 * latter redirects through the *auth* endpoint and its promise never resolves,
 * so the logout endpoint would never be reached.
 *
 * `checkLoginIframe` is disabled for that init: the login-status iframe answers
 * "unchanged" whenever it cannot read the KEYCLOAK_SESSION cookie (cross-site app
 * vs. login host, third-party cookies blocked), which keycloak-js reports as "not
 * authenticated" while the SSO session is in fact alive. Without the iframe,
 * check-sso always does a `prompt=none` round-trip, which is authoritative.
 */
export const logout = async (authConfig: AuthConfig): Promise<boolean> => {
  if (!keycloak) {
    await initKeycloak(authConfig, { onLoad: 'check-sso', checkLoginIframe: false });
  }

  if (!keycloak.authenticated) {
    return false;
  }

  await keycloak.logout({
    redirectUri: authConfig.postLogoutRedirectUrl ?? authConfig.redirectUrl,
  });
  return true;
};
